import PropTypes from "prop-types";
import { Amplify } from "aws-amplify";
import * as Sentry from "@sentry/react";
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { useRef, useMemo, useEffect, useReducer, useCallback } from "react";
import {
  signIn,
  confirmSignIn,
  confirmSignUp,
  resetPassword,
  getCurrentUser,
  resendSignUpCode,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  confirmResetPassword,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

import { Alert, Stack, Button } from '@mui/material';

import { AMPLIFY_API } from "src/config-global";
import { cookieStorageOptions } from "src/data/cookie-domain";

import { AuthContext } from "./auth-context";
import apiClient from "../../../services/ApiService";
import AuthService from "../../../services/AuthService";
import { startBrowserDevices, setBrowserDeviceOwner, disconnectBrowserDevice } from '../../../utils/browser-devices';
import { nativeSignOut, usesNativeAuth, nativeWebReady, nativeTokenProvider, subscribeNativeAuth } from '../../../utils/native-auth';

// ----------------------------------------------------------------------
/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */
// ----------------------------------------------------------------------

/**
 * DOCS: https://docs.amplify.aws/react/build-a-backend/auth/manage-user-session/
 */
const native = usesNativeAuth();
const signOut = async () => {
  if (native) return nativeSignOut();
  return disconnectBrowserDevice(amplifySignOut);
};

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: `${AMPLIFY_API.userPoolId}`,
      userPoolClientId: `${AMPLIFY_API.userPoolWebClientId}`,
    },
  },
  geo: {
    AmazonLocationService: {
      maps: {
        items: {
          HeyJob: {
            // REQUIRED - Amazon Location Service Map resource name
            style: "VectorEsriStreets", // REQUIRED - String representing the style of map resource
          },
        },
        default: "HeyJob", // REQUIRED - Amazon Location Service Map resource name to set as default
      },
      region: `${AMPLIFY_API.region}`, // REQUIRED - Amazon Location Service Region
    },
  },
}, native ? { Auth: { tokenProvider: nativeTokenProvider } } : undefined);

if (!native && Object.keys(cookieStorageOptions).includes(import.meta.env.VITE_ENV)) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(cookieStorageOptions[import.meta.env.VITE_ENV])
  );
}

const initialState = {
  user: null,
  loading: true,
  error: false,
};

const reducer = (state, action) => {
  if (action.type === "LOADING") return { ...state, loading: true, error: false };
  if (action.type === "ERROR") return { ...state, loading: false, error: true };
  if (action.type === "INITIAL") {
    return {
      loading: false,
      error: false,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      loading: false,
      error: false,
      user: null,
    };
  }
  if (action.type === "REFRESH_USER_DATA") {
    return {
      ...state,
      user: {
        ...state.user,
        data: action.payload.data,
      },
    };
  }
  if (action.type === "CONFIRM_SIGNIN") {
    return {
      ...state,
      user: null,
      email: action.payload,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    () => initialState,
  );

  const lifecycle = useRef(0);
  useEffect(() => {
    if (native) return undefined;
    const stop = startBrowserDevices();
    const signedOut = () => {
      lifecycle.current += 1;
      Sentry.setUser(null);
      dispatch({ type: 'LOGOUT' });
    };
    window.addEventListener('heyjobDeviceSignedOut', signedOut);
    return () => { stop(); window.removeEventListener('heyjobDeviceSignedOut', signedOut); };
  }, []);
  useEffect(() => {
    if (!native) setBrowserDeviceOwner(state.user?.id ?? null);
  }, [state.user?.id]);
  useEffect(() => {
    const interceptor = apiClient.interceptors.request.use(async (config) => {
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken || Number(tokens.idToken.payload.exp) * 1000 <= Date.now()) {
        throw Object.assign(new Error('Accesso necessario.'), { name: 'UserUnAuthenticatedException' });
      }
      config.headers.Authorization = `Bearer ${tokens.idToken}`;
      return config;
    });
    return () => apiClient.interceptors.request.eject(interceptor);
  }, []);

  const initialize = useCallback(async () => {
    lifecycle.current += 1;
    const generation = lifecycle.current;
    dispatch({ type: "LOADING" });
    try {
      const { userId: currentUser } = await getCurrentUser();

      const userAttributes = await fetchUserAttributes();

      const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

      const groups = accessToken.payload["cognito:groups"];

      if (currentUser) {
        if (groups && groups.length > 0 && groups.includes("Dipendente")) {

          const userDataRes = await AuthService.getUserData();
          if (generation !== lifecycle.current) return;

          if (import.meta.env.VITE_ENV !== 'local' && import.meta.env.VITE_ENV !== 'staging') {
            // Set user in Sentry
            Sentry.setUser({
              id: userAttributes.sub,
              email: userAttributes.email,
            });
          }

          dispatch({
            type: "INITIAL",
            payload: {
              user: {
                ...userAttributes,
                id: userAttributes.sub,
                displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
                idToken,
                accessToken,
                groups,
                // role: 'admin',
                data: {
                  ...userDataRes.data.user,
                },
              },
            },
          });
          if (native) nativeWebReady();
        } else {
          await signOut();

          dispatch({
            type: "INITIAL",
            payload: {
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      if (generation !== lifecycle.current) return;
      const invalid = ['UserUnAuthenticatedException', 'NotAuthorizedException', 'UserNotFoundException'].includes(error?.name);
      if (invalid) {
        // Invalid session: the browser must not keep receiving push notifications.
        if (native) nativeSignOut();
        else disconnectBrowserDevice().catch(() => {});
        dispatch({ type: 'INITIAL', payload: { user: null } });
      } else dispatch({ type: 'ERROR' });
    }
  }, []);

  useEffect(() => {
    initialize();
    if (native) window.addEventListener('heyjobNativeReady', initialize);
    const off = native ? subscribeNativeAuth(() => {
      lifecycle.current += 1;
      Sentry.setUser(null);
      dispatch({ type: 'LOGOUT' });
    }) : () => {};
    return () => { lifecycle.current += 1; off(); window.removeEventListener('heyjobNativeReady', initialize); };
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const { isSignedIn, nextStep } = await signIn({
      username: email,
      password,
    });

    switch (nextStep.signInStep) {
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        return nextStep.signInStep;
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return nextStep.signInStep;
      case "DONE":
      default:
        break;
    }

    if (isSignedIn) {
      const userAttributes = await fetchUserAttributes();

      const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

      const groups = accessToken.payload["cognito:groups"];

      if (groups && groups.length > 0 && groups.includes("Dipendente")) {

        const userDataRes = await AuthService.getUserData();

        if (import.meta.env.VITE_ENV !== 'local' && import.meta.env.VITE_ENV !== 'staging') {
          // Set user in Sentry
          Sentry.setUser({
            id: userAttributes.sub,
            email: userAttributes.email,
          });
        }

        dispatch({
          type: "INITIAL",
          payload: {
            user: {
              ...userAttributes,
              id: userAttributes.sub,
              displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
              idToken,
              accessToken,
              groups,
              data: {
                ...userDataRes.data.user,
              },
            },
          },
        });
      } else {
        await signOut();

        throw new Error("Utente non autorizzato ad accedere al portale");
      }
    }

    return "DONE";
  }, []);

  // FETCH USER DATA
  const fetchUserData = useCallback(async () => {
    const userDataRes = await AuthService.getUserData();
    dispatch({
      type: "REFRESH_USER_DATA",
      payload: {
        data: {
          ...userDataRes.data.user,
        },
      },
    });
  }, []);

  // REGISTER
  // const register = useCallback(async (email, password, firstName, lastName) => {
  //   await signUp({
  //     username: email,
  //     password,
  //     options: {
  //       userAttributes: {
  //         email,
  //         given_name: firstName,
  //         family_name: lastName,
  //       },
  //     },
  //   });
  // }, []);

  // CONFIRM REGISTER
  const confirmRegister = useCallback(async (email, code) => {
    await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
  }, []);

  const confirmLogin = useCallback(async (newPassword) => {
    await confirmSignIn({
      challengeResponse: newPassword,
    });

    const userAttributes = await fetchUserAttributes();

    const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

    const groups = accessToken.payload["cognito:groups"];

    if (groups && groups.length > 0 && groups.includes("Dipendente")) {

      const userDataRes = await AuthService.getUserData();

      if (import.meta.env.VITE_ENV !== 'local' && import.meta.env.VITE_ENV !== 'staging') {
        // Set user in Sentry
        Sentry.setUser({
          id: userAttributes.sub,
          email: userAttributes.email,
        });
      }

      dispatch({
        type: "INITIAL",
        payload: {
          user: {
            ...userAttributes,
            id: userAttributes.sub,
            displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
            idToken,
            accessToken,
            groups,
            data: {
              ...userDataRes.data.user,
            },
          },
        },
      });
    }
  }, []);

  // RESEND CODE REGISTER
  const resendCodeRegister = useCallback(async (email) => {
    await resendSignUpCode({
      username: email,
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    lifecycle.current += 1;
    await signOut();
    Sentry.setUser(null);
    dispatch({
      type: "LOGOUT",
    });
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    await resetPassword({ username: email });
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(async (email, code, password) => {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword: password,
    });
  }, []);

  const getAuthDetails = useCallback(async () => {
    const session = await fetchAuthSession();
    const mfaPreference = await fetchMFAPreference();
    return {
      ...session,
      mfa: mfaPreference,
    };
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: "amplify",
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
      //
      login,
      fetchUserData,
      logout,
      newPassword,
      forgotPassword,
      confirmRegister,
      confirmLogin,
      resendCodeRegister,
      getAuthDetails,
    }),
    [
      status,
      state.user,
      //
      login,
      fetchUserData,
      logout,
      newPassword,
      forgotPassword,
      confirmRegister,
      confirmLogin,
      resendCodeRegister,
      getAuthDetails,
    ],
  );

  let content = children;
  if (state.error) content = <Stack spacing={2} sx={{ p: 3 }}>
    <Alert severity="warning">Accesso non disponibile. Controlla la connessione e riprova.</Alert>
    <Button onClick={initialize}>Riprova</Button>
    <Button onClick={logout}>Esci</Button>
  </Stack>;
  else if (native && !state.user) content = <Stack spacing={2} sx={{ p: 3 }}>
    <Alert severity="info">Accesso in corso…</Alert>
    <Button onClick={initialize}>Riprova</Button>
    <Button onClick={logout}>Torna all’accesso</Button>
  </Stack>;
  return <AuthContext.Provider value={memoizedValue}>{content}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
